import * as LucideIcons from 'lucide-react';
import {
    LayoutDashboard, Inbox, CheckSquare, Mailbox,
    Users, Building2, List,
    Workflow, ClipboardList, CalendarDays, CalendarCheck, CalendarClock,
    GitPullRequest, KanbanSquare, Package, FileText, Layers,
    BarChart3,
    Sparkles, HeartPulse, Bot, ShieldCheck,
    Settings, Shield,
    Users as UsersIcon, Monitor, BarChart3 as BarChartIcon,
    User, Mic, Link, Crown, Puzzle, Clock, Presentation, Bell,
    FileSignature, Tag, Target, LayoutGrid, TrendingUp, Database, Image,
    Mail,
    Briefcase,
    BookOpen, Brain, BadgeCheck, House,
    Code,
} from 'lucide-react';

// ─── Types (JSDoc for IDE support) ────────────────────────────────────────────

/**
 * @typedef {Object} WorkspaceSidebarItem
 * @property {string} id - Unique item ID (kebab-case)
 * @property {string} label - Display label
 * @property {import('lucide-react').LucideIcon} icon - Lucide icon component
 * @property {string} path - Route path
 * @property {string} [badge] - Badge key ('tasks', 'reviews', 'inbox', 'pending') for dynamic counts
 * @property {'default'|'danger'} [badgeVariant] - Badge color variant
 * @property {string} [feature] - Plan feature gate key
 * @property {string} [permission] - Runtime permission required to show this item
 * @property {boolean} [adminOnly] - Only visible to admins
 * @property {boolean} [agentDeveloper] - Only visible to agent developers/admins
 * @property {boolean} [inboxAccess] - Only visible to admins OR users holding at
 *   least one unified-inbox access grant. Resolved in ContextualSidebar, which is
 *   the only place that can ask the API — the answer is not on the user object.
 * @property {boolean} [pitchbookAccess] - Only visible to admins when PitchBook
 *   is configured (GET /api/pitchbook/config returns 200). Resolved in
 *   ContextualSidebar, mirroring the inboxAccess pattern.
 * @property {boolean} [ai] - AI tool item (shows amber dot)
 * @property {string} [group] - Id of a group declared in the section's `groups`.
 *   Members stay flat in `items`; ContextualSidebar renders them together under
 *   one disclosure, at the position of the group's first visible member.
 */

/**
 * @typedef {Object} WorkspaceSidebarGroup
 * @property {string} id - Referenced by member items' `group`
 * @property {string} label - Disclosure label
 * @property {import('lucide-react').LucideIcon} [icon]
 * @property {boolean} [collapsible] - Render as a disclosure (else a static sub-heading)
 * @property {boolean} [defaultOpen] - Open when the viewer has no stored preference
 */

/**
 * @typedef {Object} Workspace
 * @property {string} id - Unique workspace ID
 * @property {string} name - Display name
 * @property {string} hue - Registry-driven identity color (rail tile, --ws, active states)
 * @property {import('lucide-react').LucideIcon} glyph - Lucide component (monochrome; rail + hub tiles)
 * @property {'native'|'module-iframe'|'module-native'} type - Native page, provider iframe, or shell-mounted custom module
 * @property {'active'|'available'|'coming-soon'} status - Installation status
 * @property {string} [providerId] - Module provider ID (e.g. 'prov_content_library')
 * @property {string} [moduleSlug] - Custom module slug (type 'module-native' only)
 * @property {string} [moduleKey] - Plan module key for gating (e.g. 'ats')
 * @property {WorkspaceSection[]} sections - Sidebar sections
 * @property {string} [permission] - Runtime permission required to show the workspace
 * @property {WorkspaceSidebarItem[]} [footerItems] - Footer items (retained for schema compatibility; empty for all workspaces after shell cleanup)
 */

// ─── CRM Core Workspace (native, always active) ──────────────────────────────

const CRM_CORE = {
    id: 'crm-core',
    name: 'CRM Core',
    hue: '#3E63DD',
    // Briefcase is the canonical CRM metaphor (deals, pipelines, contacts) and
    // visually distinguishes CRM Core from the generic "people" icon that the
    // Users component renders — the rail tile and hub card both use this glyph.
    glyph: Briefcase,
    quickActions: [
        { id: 'new-deal', label: 'New deal', icon: GitPullRequest, path: '/pipeline' },
        { id: 'new-contact', label: 'New contact', icon: Users, path: '/contacts' },
        { id: 'new-company', label: 'New company', icon: Building2, path: '/companies' },
        { id: 'new-campaign', label: 'New campaign', icon: Workflow, path: '/campaigns' },
        { id: 'new-form', label: 'New form', icon: ClipboardList, path: '/forms' },
    ],
    type: 'native',
    status: 'active',
    sections: [
        {
            id: 'overview',
            label: 'Overview',
            // #675: a collapsible Scheduling group. The members stay FLAT in
            // `items` and name their group, rather than nesting as children —
            // every reader of this file (the TopBar crumb, the command palette,
            // WorkspaceContext's path matching) walks `section.items` flat, and a
            // nested array would have hidden Calendar and Booking Links from all
            // of them. Only the sidebar renderer knows groups exist.
            groups: [
                { id: 'scheduling', label: 'Scheduling', icon: CalendarClock, collapsible: true, defaultOpen: true },
            ],
            items: [
                { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
                { id: 'inbox', label: 'Inboxes', icon: Inbox, path: '/inbox', badge: 'inbox' },
                { id: 'tasks', label: 'Tasks', icon: CheckSquare, path: '/tasks', badge: 'tasks', badgeVariant: 'danger' },
                { id: 'calendar', label: 'Calendar', icon: CalendarCheck, path: '/calendar', group: 'scheduling' },
                { id: 'booking-links', label: 'Booking Links', icon: Link, path: '/booking-links', group: 'scheduling' },
            ],
        },
        {
            id: 'crm',
            label: 'CRM',
            items: [
                { id: 'contacts', label: 'Contacts', icon: Users, path: '/contacts' },
                { id: 'companies', label: 'Companies', icon: Building2, path: '/companies' },
                { id: 'activities', label: 'Activities', icon: List, path: '/activities' },
            ],
        },
        {
            id: 'marketing',
            label: 'Marketing',
            items: [
                { id: 'campaigns', label: 'Campaigns', icon: Workflow, path: '/campaigns' },
                { id: 'forms', label: 'Forms', icon: ClipboardList, path: '/forms' },
                { id: 'events', label: 'Events', icon: CalendarDays, path: '/events' },
            ],
        },
        {
            id: 'sales',
            label: 'Sales',
            items: [
                { id: 'pipeline', label: 'Deal Pipeline', icon: GitPullRequest, path: '/pipeline' },
                { id: 'lead-pipeline', label: 'Lead Pipeline', icon: KanbanSquare, path: '/lead-pipeline' },
                { id: 'products', label: 'Products', icon: Package, path: '/products' },
                { id: 'proposals', label: 'Proposals', icon: FileText, path: '/proposals' },
                { id: 'deal-rooms', label: 'Deal Rooms', icon: Layers, path: '/deal-rooms' },
            ],
        },
        {
            id: 'insights',
            label: 'Insights',
            items: [
                { id: 'reports', label: 'Reports', icon: BarChart3, path: '/reports' },
                { id: 'list-management', label: 'List Management', icon: List, path: '/list-management', adminOnly: true, feature: 'list_management' },
            ],
        },
        {
            id: 'ai',
            label: 'AI',
            items: [
                { id: 'ai-assistant', label: 'AI Assistant', icon: Sparkles, path: '/ai-assistant', ai: true, feature: 'ai_assistant' },
                { id: 'ai-heartbeat', label: 'AI Heartbeat', icon: HeartPulse, path: '/ai-heartbeat', adminOnly: true, feature: 'ai_heartbeat' },
                { id: 'ai-reviews', label: 'AI Reviews', icon: ShieldCheck, path: '/ai-reviews', badge: 'reviews', feature: 'agentic_personalization' },
                { id: 'ai-agents', label: 'AI Agents', icon: Bot, path: '/agents', agentDeveloper: true },
            ],
        },
    ],
    footerItems: [], // Footer removed — Settings, Admin, and user actions now live in the WorkspaceRail
};

// ─── Built-in Wiki workspace ─────────────────────────────────────────────────
// The Wiki is a core runtime surface, not a provider module. Keep the rail entry
// visible so restricted users receive the product's in-page access state; the
// Wiki API and item-level Brain permissions remain authoritative for access.
//
// The sidebar is ONE section rendered by `pages/wiki/nav/WikiNav.jsx`
// (ContextualSidebar's `render: 'wiki-nav'` hook): Home / Ask the Wiki / To
// acknowledge, the data-backed CATEGORIES tree and MANAGE › AI Brain, built to
// the prototype (REBUILD-PLAN §1). Its `items` are NOT rendered as rows; they
// stay declared because every other reader of this registry walks
// `section.items` — `setCurrentWorkspace` (rail click lands on items[0]), the
// command palette, page IDs (`derivePageId`) and the TopBar fallback crumb.
// Hue = the prototype's Wiki teal; it feeds the rail tile and `--ws`.
const WIKI_WORKSPACE = {
    id: 'wiki',
    name: 'Wiki',
    hue: '#0F766E',
    glyph: BookOpen,
    type: 'native',
    status: 'active',
    sections: [
        {
            id: 'wiki-nav',
            label: 'Wiki',
            render: 'wiki-nav',
            items: [
                { id: 'wiki-home', label: 'Home', icon: House, path: '/wiki' },
                { id: 'wiki-ask', label: 'Ask the Wiki', icon: Sparkles, path: '/wiki/ask' },
                { id: 'wiki-acknowledge', label: 'To acknowledge', icon: BadgeCheck, path: '/wiki/acknowledge' },
                { id: 'wiki-brain', label: 'AI Brain', icon: Brain, path: '/wiki/brain', permission: 'wiki:brain_view' },
            ],
        },
    ],
    footerItems: [],
    // The TopBar "New" button in the Wiki is wired in shell/TopBar.jsx (New
    // page modal for editors, the prototype's toast for Members) — not here.
    quickActions: [],
};

// ─── Settings Virtual Workspace (account-level settings) ────────────────────
// A virtual workspace: type 'native', status 'active'. It flows through the
// existing enrichedWorkspaces pipeline and appears in the ContextualSidebar
// when active. It does NOT appear as a tile in the WorkspaceRail (filtered out
// by the rail's `ws.id !== 'settings' && ws.id !== 'admin'` check).

const SETTINGS_WORKSPACE = {
    id: 'settings',
    name: 'Settings',
    hue: '#6B7280', // neutral gray
    glyph: Settings,
    type: 'native',
    status: 'active',
    sections: [
        {
            id: 'account',
            label: 'Account',
            items: [
                { id: 'profile', label: 'Profile', icon: User, path: '/settings/profile' },
                { id: 'voice-capture', label: 'Voice Capture', icon: Mic, path: '/settings/voice-capture' },
                { id: 'integrations', label: 'Integrations', icon: Link, path: '/settings/integrations' },
                { id: 'ai-assistant', label: 'AI Assistant', icon: Sparkles, path: '/settings/ai-assistant' },
                { id: 'security', label: 'Security', icon: Shield, path: '/settings/security' },
                { id: 'templates-signatures', label: 'Templates & Signatures', icon: FileSignature, path: '/settings/templates-signatures' },
            ],
        },
    ],
    footerItems: [],
    quickActions: [],
};

// ─── Admin Virtual Workspace (org-level admin) ─────────────────────────────
// Same virtual workspace pattern as Settings. Organization consolidates the
// former TeamMembersTab (from Admin) and OrganizationSettings (from Settings)
// into a single scrolling page. Plan & Usage and Modules moved here from
// Settings because they are org-level, not account-level.

const ADMIN_WORKSPACE = {
    id: 'admin',
    name: 'Admin',
    hue: '#7C3AED', // violet
    glyph: Shield,
    type: 'native',
    status: 'active',
    sections: [
        {
            id: 'organization',
            label: 'Organization',
            items: [
                { id: 'organization', label: 'Organization', icon: Building2, path: '/admin/organization' },
                { id: 'notifications', label: 'Notifications', icon: Bell, path: '/admin/notifications', adminOnly: true },
                { id: 'pending-approvals', label: 'Pending Approvals', icon: Clock, path: '/admin/pending-approvals', badge: 'pending', badgeVariant: 'danger', adminOnly: true },
            ],
        },
        {
            id: 'team',
            label: 'Team',
            items: [
                { id: 'coaches', label: 'Coaches', icon: Presentation, path: '/admin/coaches' },
                { id: 'user-groups', label: 'User Groups', icon: Users, path: '/admin/user-groups' },
            ],
        },
        {
            id: 'templates',
            label: 'Templates',
            items: [
                { id: 'deal-room-templates', label: 'Deal Room Templates', icon: Layers, path: '/admin/deal-room-templates' },
                { id: 'email-templates', label: 'Email Templates', icon: Mail, path: '/admin/email-templates' },
                { id: 'signatures', label: 'Signatures', icon: FileSignature, path: '/admin/signatures' },
            ],
        },
        {
            id: 'ai',
            label: 'AI',
            items: [
                { id: 'ai-skills', label: 'AI Skills', icon: Sparkles, path: '/admin/ai-skills' },
                { id: 'ai-usage', label: 'AI Usage', icon: TrendingUp, path: '/admin/ai-usage' },
            ],
        },
        {
            // #793 — Developer API. Its own section, placed before System so it
            // stays above the fold. Settings already has an "Integrations" ITEM;
            // the command palette lists items, not sections, so the labels stay
            // distinct ("Integrations" vs "Developer API").
            id: 'integrations',
            label: 'Integrations',
            items: [
                { id: 'developer-api', label: 'Developer API', icon: Code, path: '/admin/developer-api', adminOnly: true },
            ],
        },
        {
            id: 'system',
            label: 'System',
            items: [
                { id: 'plan-usage', label: 'Plan & Usage', icon: Crown, path: '/admin/plan-usage', adminOnly: true },
                { id: 'modules', label: 'Modules', icon: Puzzle, path: '/admin/modules', adminOnly: true },
                { id: 'media-library', label: 'Media Library', icon: Image, path: '/admin/media-library' },
                { id: 'voice-profiles', label: 'Voice Profiles', icon: Mic, path: '/admin/voice-profiles' },
                { id: 'tags', label: 'Tags', icon: Tag, path: '/admin/tags' },
                { id: 'custom-fields', label: 'Custom Fields', icon: Layers, path: '/admin/custom-fields' },
                { id: 'lead-pipeline', label: 'Lead Pipeline', icon: Target, path: '/admin/lead-pipeline' },
                { id: 'pipelines', label: 'Pipelines', icon: Workflow, path: '/admin/pipelines', adminOnly: true },
                { id: 'event-types', label: 'Event Types', icon: CalendarDays, path: '/admin/event-types' },
                { id: 'product-settings', label: 'Product Settings', icon: Package, path: '/admin/product-settings' },
                { id: 'app-launcher', label: 'App Launcher', icon: LayoutGrid, path: '/admin/app-launcher' },
                { id: 'unified-inbox', label: 'Unified Inbox', icon: Mailbox, path: '/admin/unified-inbox', adminOnly: true },
                { id: 'pitchbook-sync', label: 'PitchBook Sync', icon: Database, path: '/admin/pitchbook-sync', adminOnly: true, pitchbookAccess: true },
            ],
        },
    ],
    footerItems: [],
    quickActions: [],
};

// ─── Dynamic Module Provider Workspaces ──────────────────────────────────────
// Module provider workspaces are NO LONGER hardcoded here. They are generated
// dynamically at runtime from the /v1/modules/providers API response, which
// reads PROVIDER_CONFIGS injected by the control plane. This allows the
// control plane to add new module providers without rebuilding the runtime image.
//
// See WorkspaceContext.jsx → enrichedWorkspaces for the dynamic generation logic.

// Default color palette for dynamically generated workspaces.
// Colors are assigned deterministically by hashing the provider ID.
const DYNAMIC_HUES = [
    '#00A2C7', '#6E56CF', '#D6409F', '#E8590C',
    '#12A594', '#E5484D', '#946300', '#1A7F4E',
];

function hashString(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash |= 0; // Force 32-bit integer
    }
    return Math.abs(hash);
}

/**
 * Create a workspace object from a provider API response.
 * Used by WorkspaceContext to dynamically add module-iframe workspaces
 * for providers that are installed (returned by /v1/modules/providers).
 *
 * @param {Object} provider - Provider object from /v1/modules/providers
 * @param {string} provider.provider_id - Unique provider ID
 * @param {string} [provider.name] - Display name
 * @param {string} [provider.url] - Iframe URL
 * @param {Array} [provider.navigation] - Navigation items
 * @returns {Workspace}
 */
export function createDynamicWorkspace(provider) {
    const pid = provider.provider_id || provider.id;
    const hueIndex = hashString(pid) % DYNAMIC_HUES.length;
    return {
        id: pid,
        name: provider.name || pid,
        hue: DYNAMIC_HUES[hueIndex],
        glyph: Layers,
        type: 'module-iframe',
        status: 'active', // It's installed — the API only returns installed providers
        providerId: pid,
        sections: [],
        footerItems: [],
        quickActions: [],
    };
}

// ─── Custom module workspaces (plan Task 7.7, D17) ───────────────────────────
//
// A custom module declares its whole workspace in its `manifest.json`, which maps 1:1
// onto the registry v2 shape above — that mapping is why nav is statically auditable
// before the control plane approves a bundle, rather than something the module asserts
// at runtime.
//
// A SIBLING of createDynamicWorkspace rather than an extension of it. That function
// hardcodes `type: 'module-iframe'` and `glyph: Layers` because a provider has neither
// a declared glyph nor a hue and is rendered in an iframe; a custom module declares
// both and is mounted directly. Forcing one function to serve both would mean a
// parameter that switches every line of its body.

/**
 * Resolve a manifest's glyph name to a lucide-react component.
 *
 * Falls back to `Layers` with a console warning rather than throwing. A typo'd or
 * renamed glyph must not take the workspace down — the rail tile is the module's only
 * entry point, so a crash here would make the module unreachable over a cosmetic
 * mistake. The warning is what makes it fixable.
 *
 * Guards against a non-component export too: `lucide-react` exports helpers and the
 * `icons` map alongside its components, so a manifest naming one of those would
 * otherwise hand React something it cannot render.
 *
 * @param {string} [name] - lucide-react export name from the manifest
 * @param {import('lucide-react').LucideIcon} [fallback]
 */
export function resolveGlyph(name, fallback = Layers) {
    if (!name || typeof name !== 'string') return fallback;
    const candidate = LucideIcons[name];
    const isComponent = typeof candidate === 'function'
        || (candidate && typeof candidate === 'object' && candidate.$$typeof);
    if (!isComponent) {
        console.warn(
            `[workspaces] glyph '${name}' is not a lucide-react icon; falling back to Layers`,
        );
        return fallback;
    }
    return candidate;
}

/**
 * Build a workspace from a custom module's nav payload.
 *
 * Input is one entry from `GET /api/v1/modules/custom` — the manifest's `navigation`
 * block plus the asset URLs the loader computed. Only healthy modules are returned by
 * that endpoint, so the workspace is unconditionally `active`: a rail tile for a module
 * whose routes were never mounted is a link to a 404.
 *
 * @param {Object} module - entry from /v1/modules/custom
 * @returns {Workspace|null} - null when the module declares no navigation
 */
export function createCustomModuleWorkspace(module) {
    const nav = module?.navigation;
    if (!nav) return null;

    const mapItem = (item) => ({
        id: item.id,
        label: item.label,
        icon: resolveGlyph(item.glyph),
        path: item.path,
    });

    return {
        // The nav id, not the slug: `navigation.id` is what the manifest declares as the
        // workspace identity, and the slug is the delivery identifier. Both are carried,
        // because the shell keys workspaces by `id` and the module host needs the slug.
        id: nav.id,
        moduleSlug: module.slug,
        name: nav.name || module.name || module.slug,
        hue: nav.hue,
        glyph: resolveGlyph(nav.glyph),
        type: 'module-native',
        status: 'active',
        version: module.version,
        basePath: module.base_path,
        entryUrl: module.entry_url,
        sections: (nav.sections || []).map((section) => ({
            id: section.id,
            label: section.label,
            items: (section.items || []).map(mapItem),
        })),
        footerItems: (nav.footerItems || []).map(mapItem),
        quickActions: (nav.quickActions || []).map(mapItem),
    };
}

// ─── Coming-soon placeholders (not real providers yet) ────────────────────────

const HR = {
    id: 'hr', name: 'HR', hue: '#8B857B', glyph: UsersIcon,
    type: 'module-iframe', status: 'coming-soon',
    sections: [], footerItems: [], quickActions: [],
};

const LEGAL = {
    id: 'legal', name: 'Legal', hue: '#8B857B', glyph: Shield,
    type: 'module-iframe', status: 'coming-soon',
    sections: [], footerItems: [], quickActions: [],
};

const IT = {
    id: 'it', name: 'IT', hue: '#8B857B', glyph: Monitor,
    type: 'module-iframe', status: 'coming-soon',
    sections: [], footerItems: [], quickActions: [],
};

const FINANCE = {
    id: 'finance', name: 'Finance', hue: '#8B857B', glyph: BarChartIcon,
    type: 'module-iframe', status: 'coming-soon',
    sections: [], footerItems: [], quickActions: [],
};

// ─── Export ──────────────────────────────────────────────────────────────────
//
// Only CRM Core is active by default. Module provider workspaces are generated
// dynamically from the API response (see WorkspaceContext.jsx).
// Coming-soon placeholders are static.

export const WORKSPACES = [
    CRM_CORE,
    WIKI_WORKSPACE,
    SETTINGS_WORKSPACE,
    ADMIN_WORKSPACE,
    HR,
    LEGAL,
    IT,
    FINANCE,
];

// Helper: get workspace by ID
export function getWorkspaceById(id) {
    return WORKSPACES.find(ws => ws.id === id);
}

// Helper: get active workspaces only.
// Accepts an optional `workspaces` array so callers can pass the
// dynamically-enriched list from WorkspaceContext (which includes
// API-generated module workspaces). Defaults to the static WORKSPACES.
export function getActiveWorkspaces(workspaces = WORKSPACES) {
    return workspaces.filter(ws => ws.status === 'active');
}

// Helper: determine if a workspace is active.
// A workspace is active if:
//   1. Its status is 'active' (always visible), OR
//   2. Its status is 'available' AND it has a providerId AND that provider is installed.
//
// Custom modules (type 'module-native') need no extra clause: the runtime only serves
// nav for a module that actually loaded, so a custom-module workspace exists in the
// merged list if and only if it is live, and it is built with status 'active'. Adding an
// installed-check here would ask the frontend to re-decide something the loader already
// decided with much better information.
export function isWorkspaceActive(ws, isInstalledFn) {
    if (!ws) return false;
    if (ws.status === 'active') return true;
    if (ws.status === 'available' && ws.providerId && isInstalledFn && isInstalledFn(ws.providerId)) return true;
    return false;
}

// Helper: flatten all sidebar items across all active workspaces (for command palette)
// Accepts an optional `workspaces` array so callers can pass the
// dynamically-enriched list from WorkspaceContext (which includes
// API-generated module workspaces). Defaults to the static WORKSPACES.
export function getAllSidebarItems(workspaces = WORKSPACES) {
    const items = [];
    for (const ws of workspaces) {
        if (ws.status !== 'active') continue;
        for (const section of ws.sections) {
            for (const item of (section.items || [])) {
                items.push({ workspace: ws, section, item });
            }
        }
    }
    return items;
}

// Helper: derive the active sidebar item's label from a pathname.
// Falls back to the first sidebar item when nothing matches; used by the TopBar crumb.
export function getActiveItemLabel(ws, pathname) {
    if (!ws || !ws.sections) return '';
    let best = null;
    let bestLen = -1;
    for (const section of ws.sections) {
        for (const item of (section.items || [])) {
            const raw = item.path || '';
            const [pathOnly] = raw.split('?');
            if (!pathOnly) continue;
            if (pathname === pathOnly || (pathOnly !== '/' && pathname.startsWith(pathOnly))) {
                if (pathOnly.length > bestLen) {
                    best = item;
                    bestLen = pathOnly.length;
                }
            }
        }
    }
    if (best) return best.label;
    const first = ws.sections[0]?.items?.[0];
    return first ? first.label : '';
}
